/**
 * Business-level OpenTelemetry helpers.
 *
 * Decision (see docs/behavior-map/SESSION_TRACE.md): one `pwa.session` root per tab.
 * Business journeys (`request.load`, `send.process`, …) and HTTP nest under it.
 *
 * pwa.session
 * ├── request.load                    (app.layer=journey)
 * │   ├── HTTP …
 * │   └── app.* diary                 (app.layer=diary, nested)
 * ├── document
 * └── send.process
 *       ├── prepare.process
 *       ├── document.upload
 *       └── request.complete
 * UI chrome (popups) → root events only (app.layer=ui).
 */
import {
  context,
  trace,
  SpanStatusCode,
  ROOT_CONTEXT
} from '@opentelemetry/api';
import {
  isTelemetryStarted,
  OTEL_ATTR_REQUEST_R,
  getRequestParamR
} from './otel';
import {
  startSessionTrace,
  endSessionTrace,
  recordSessionEvent,
  getSessionTraceId,
  updateSessionRequestId,
  getSessionContext as getSessionTraceContext
} from './sessionTrace';
import { APP_LAYER, OTEL_ATTR_APP_LAYER } from './analyticsLayer';

const TRACER_NAME = 'paperfly-pwa';

export const JOURNEY = {
  LOAD: 'load',
  SUBMIT: 'submit',
  PREPARE: 'prepare',
  CREDENTIALS: 'credentials',
  DOCUMENT: 'document',
  HANDOFF: 'handoff'
};

/** Top-level journeys under pwa.session (prepare/credentials nest under submit). */
const ROOT_JOURNEYS = new Set([
  JOURNEY.LOAD,
  JOURNEY.DOCUMENT,
  JOURNEY.SUBMIT,
  JOURNEY.HANDOFF
]);

/** @type {{ sessionId: string } | null} */
let session = null;

/** Export parents before children so Jaeger can link open journey trees. */
const JOURNEY_EXPORT_ORDER = [
  JOURNEY.LOAD,
  JOURNEY.DOCUMENT,
  JOURNEY.SUBMIT,
  JOURNEY.PREPARE,
  JOURNEY.CREDENTIALS,
  JOURNEY.HANDOFF
];

/** @type {Record<string, {
 *  span: import('@opentelemetry/api').Span | null,
 *  ctx: import('@opentelemetry/api').Context,
 *  spanContext: import('@opentelemetry/api').SpanContext,
 *  name: string,
 *  startTime: [number, number],
 *  attributes: Record<string, string | number | boolean>,
 *  exportInjected: boolean
 * }>} */
const journeys = Object.create(null);

function hrTimeNow() {
  const now = Date.now();
  return [Math.floor(now / 1000), (now % 1000) * 1e6];
}

function getTracer() {
  return trace.getTracer(TRACER_NAME);
}

function currentSessionId() {
  if (session && session.sessionId) return session.sessionId;
  if (typeof window !== 'undefined' && window.pwaSession) {
    return window.pwaSession;
  }
  return '';
}

function sanitizeAttributes(attrs) {
  const out = {};
  if (!attrs) return out;
  Object.keys(attrs).forEach(key => {
    const value = attrs[key];
    if (value === undefined || value === null) return;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      out[key] = value;
    } else {
      out[key] = String(value);
    }
  });
  const requestId = getRequestParamR();
  if (requestId && out[OTEL_ATTR_REQUEST_R] === undefined) {
    out[OTEL_ATTR_REQUEST_R] = requestId;
  }
  const sessionId = currentSessionId();
  if (sessionId && out['pwa.session_id'] === undefined) {
    out['pwa.session_id'] = sessionId;
  }
  return out;
}

function setAttrs(span, attrs) {
  const clean = sanitizeAttributes(attrs);
  Object.keys(clean).forEach(k => span.setAttribute(k, clean[k]));
}

/**
 * Nest prepare/credentials under send; handoff under document when open;
 * load/document/submit under pwa.session (same traceId — not separate roots).
 */
function sessionOrRoot() {
  return getSessionTraceContext() || ROOT_CONTEXT;
}

function pickParentContext(journeyKey) {
  if (journeyKey === JOURNEY.CREDENTIALS) {
    return (
      (journeys[JOURNEY.PREPARE] && journeys[JOURNEY.PREPARE].ctx) ||
      (journeys[JOURNEY.SUBMIT] && journeys[JOURNEY.SUBMIT].ctx) ||
      sessionOrRoot()
    );
  }
  if (journeyKey === JOURNEY.PREPARE) {
    return (
      (journeys[JOURNEY.SUBMIT] && journeys[JOURNEY.SUBMIT].ctx) ||
      sessionOrRoot()
    );
  }
  // Cross-device / sign-on-mobile: nest under open document when present
  // so Jaeger shows document → handoff.* → guest pwa.session.
  if (journeyKey === JOURNEY.HANDOFF) {
    return (
      (journeys[JOURNEY.DOCUMENT] && journeys[JOURNEY.DOCUMENT].ctx) ||
      sessionOrRoot()
    );
  }
  // load / document / submit → under pwa.session
  return sessionOrRoot();
}

function activeBusinessContext() {
  if (trace.getSpan(context.active())) {
    return context.active();
  }
  return getAmbientContext() || sessionOrRoot();
}

/**
 * Run async/sync work as a child of the active journey (or session).
 */
export function withSpan(name, attributes, fn) {
  if (!isTelemetryStarted()) {
    return typeof fn === 'function' ? fn(null) : undefined;
  }

  const parentCtx = activeBusinessContext();
  const span = getTracer().startSpan(
    name,
    {
      attributes: sanitizeAttributes({
        [OTEL_ATTR_APP_LAYER]: APP_LAYER.JOURNEY,
        ...(attributes || {})
      })
    },
    parentCtx
  );
  const spanCtx = trace.setSpan(parentCtx, span);

  return context.with(spanCtx, () => {
    let result;
    try {
      result = fn(span);
    } catch (err) {
      failSpan(span, err);
      span.end();
      throw err;
    }

    if (result && typeof result.then === 'function') {
      return result.then(
        value => {
          okSpan(span);
          span.end();
          return value;
        },
        err => {
          failSpan(span, err);
          span.end();
          throw err;
        }
      );
    }

    okSpan(span);
    span.end();
    return result;
  });
}

export function okSpan(span, attributes) {
  if (!span) return;
  if (attributes) setAttrs(span, attributes);
  span.setStatus({ code: SpanStatusCode.OK });
}

export function failSpan(span, err, attributes) {
  if (!span) return;
  if (attributes) setAttrs(span, attributes);
  if (err) {
    if (err instanceof Error) {
      span.recordException(err);
      span.setStatus({ code: SpanStatusCode.ERROR, message: err.message });
    } else {
      const message = String(err);
      span.setAttribute('error.message', message);
      span.setStatus({ code: SpanStatusCode.ERROR, message });
    }
  } else {
    span.setStatus({ code: SpanStatusCode.ERROR });
  }
}

export function rejectSpan(span, reason, attributes) {
  if (!span) return;
  if (attributes) setAttrs(span, attributes);
  if (reason) span.setAttribute('app.rejection_reason', String(reason));
  span.setStatus({ code: SpanStatusCode.OK });
  span.setAttribute('app.business_rejection', true);
}

/**
 * Start chronological `pwa.session` timeline (ts-switch-min shape) + bind id.
 */
export function startSession(attributes) {
  if (!isTelemetryStarted()) return null;
  if (session) return null;

  const sessionId =
    (typeof window !== 'undefined' && window.pwaSession) || 'unknown';
  session = { sessionId };

  startSessionTrace(getRequestParamR());
  if (attributes && typeof attributes === 'object') {
    recordSessionEvent('session.attrs', sanitizeAttributes(attributes));
  }
  return sessionId;
}

export function endSession(outcome, detail) {
  // Close open journeys. SUBMIT cascades prepare/credentials; list
  // prepare/credentials after for orphan cases without an open send.
  [
    JOURNEY.DOCUMENT,
    JOURNEY.LOAD,
    JOURNEY.HANDOFF,
    JOURNEY.SUBMIT,
    JOURNEY.PREPARE,
    JOURNEY.CREDENTIALS
  ].forEach(key => {
    if (journeys[key]) {
      endJourney(key, outcome === 'error' ? 'error' : 'ok');
    }
  });

  const reason =
    (detail &&
      typeof detail === 'object' &&
      (detail['app.end_reason'] || detail['session.end_reason'])) ||
    (outcome === 'error' ? 'error' : 'pagehide');
  endSessionTrace(String(reason));
  session = null;

  try {
    const otel = require('./otel');
    if (typeof otel.forceFlushTelemetry === 'function') {
      otel.forceFlushTelemetry();
    }
  } catch (e) {
    // ignore
  }
}

export function getSessionId() {
  return currentSessionId() || null;
}

/**
 * Keep pwa.session root request_id in sync (e.g. standalone new /t= template).
 */
export function noteActiveRequestId(requestId, detail) {
  if (!requestId) return false;
  return updateSessionRequestId(requestId, detail);
}

export { getSessionSpan, getSessionContext } from './sessionTrace';

/** Deepest open journey, else pwa.session — for Fetch/XHR nesting. */
export function getAmbientContext() {
  if (journeys[JOURNEY.CREDENTIALS]) return journeys[JOURNEY.CREDENTIALS].ctx;
  if (journeys[JOURNEY.PREPARE]) return journeys[JOURNEY.PREPARE].ctx;
  // Prefer handoff over document so polls/HTTP nest under handoff.cross_device
  // while the QR wait is active (document stays the parent of handoff itself).
  if (journeys[JOURNEY.HANDOFF]) return journeys[JOURNEY.HANDOFF].ctx;
  if (journeys[JOURNEY.DOCUMENT]) return journeys[JOURNEY.DOCUMENT].ctx;
  if (journeys[JOURNEY.SUBMIT]) return journeys[JOURNEY.SUBMIT].ctx;
  if (journeys[JOURNEY.LOAD]) return journeys[JOURNEY.LOAD].ctx;
  return getSessionTraceContext() || null;
}

/** Recording span for the deepest open journey (for gae breadcrumbs). */
export function getAmbientRecordingSpan() {
  const order = [
    JOURNEY.CREDENTIALS,
    JOURNEY.PREPARE,
    JOURNEY.HANDOFF,
    JOURNEY.DOCUMENT,
    JOURNEY.SUBMIT,
    JOURNEY.LOAD
  ];
  for (let i = 0; i < order.length; i++) {
    const span = getJourneySpan(order[i]);
    if (span && typeof span.isRecording === 'function' && span.isRecording()) {
      return span;
    }
  }
  return null;
}

export function runInSession(fn) {
  try {
    const { runWithSessionContext } = require('./sessionTrace');
    return runWithSessionContext(() =>
      typeof fn === 'function' ? fn(null) : undefined
    );
  } catch (e) {
    return typeof fn === 'function' ? fn(null) : undefined;
  }
}

/**
 * Run work under deepest open journey (handoff/document/send/…), else session.
 * Prefer this over runInSession for API calls so HTTP nests under the journey.
 */
export function runInAmbient(fn) {
  const ctx = getAmbientContext();
  if (!ctx) {
    return typeof fn === 'function' ? fn() : undefined;
  }
  return context.with(ctx, () => (typeof fn === 'function' ? fn() : undefined));
}

/**
 * Start a business journey under pwa.session (same traceId).
 * prepare/credentials nest under send when active.
 */
export function beginJourney(journeyKey, spanName, attributes) {
  if (!isTelemetryStarted()) return null;

  if (journeyKey === JOURNEY.SUBMIT || journeyKey === JOURNEY.DOCUMENT) {
    if (journeys[JOURNEY.DOCUMENT] && journeyKey !== JOURNEY.DOCUMENT) {
      endJourney(JOURNEY.DOCUMENT, 'ok', {
        'document.end_reason': 'send_started'
      });
    }
  }
  if (journeyKey === JOURNEY.DOCUMENT && journeys[JOURNEY.DOCUMENT]) {
    endJourney(JOURNEY.DOCUMENT, 'ok', {
      'document.end_reason': 'next_document'
    });
  }

  endJourney(journeyKey);

  const parentCtx = pickParentContext(journeyKey);
  const attrs = sanitizeAttributes({
    [OTEL_ATTR_APP_LAYER]: APP_LAYER.JOURNEY,
    ...(attributes || {})
  });
  const span = getTracer().startSpan(
    spanName,
    {
      attributes: attrs
    },
    parentCtx
  );
  const spanContext = span.spanContext();
  const ctx = trace.setSpan(parentCtx, span);
  journeys[journeyKey] = {
    span,
    ctx,
    spanContext,
    name: spanName,
    startTime: hrTimeNow(),
    attributes: { ...attrs },
    exportInjected: false
  };

  if (
    process.env.REACT_APP_ENV !== 'production' &&
    ROOT_JOURNEYS.has(journeyKey)
  ) {
    console.log(
      '[otel] journey',
      spanName,
      'session',
      currentSessionId(),
      'traceId',
      spanContext.traceId,
      'sessionTrace',
      getSessionTraceId() || '(none)'
    );
  }

  // Handoff QR embeds this span id — export before the guest can load.
  if (journeyKey === JOURNEY.HANDOFF) {
    try {
      const otel = require('./otel');
      if (typeof otel.forceFlushTelemetry === 'function') {
        otel.forceFlushTelemetry();
      }
    } catch (e) {
      // ignore
    }
  }
  return span;
}

/**
 * Inject still-open journeys into an OTLP batch (same idea as session root).
 * Needed so QR `traceparent` parents exist in Jaeger before handoff ends.
 */
export function buildOpenJourneyExportSpans(existingSpans) {
  const inBatch = new Set(
    (existingSpans || [])
      .map(s => {
        try {
          return s.spanContext && s.spanContext().spanId;
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean)
  );
  const out = [];
  JOURNEY_EXPORT_ORDER.forEach(key => {
    const journey = journeys[key];
    if (!journey || !journey.span || journey.exportInjected) return;
    const sid = journey.spanContext && journey.spanContext.spanId;
    if (!sid || inBatch.has(sid)) return;

    const span = journey.span;
    const parentSpanContext =
      span.parentSpanContext ||
      (span.parentSpanId
        ? {
            traceId: journey.spanContext.traceId,
            spanId: span.parentSpanId,
            traceFlags: 1
          }
        : undefined);
    const liveAttrs =
      span.attributes && typeof span.attributes === 'object'
        ? { ...journey.attributes, ...span.attributes }
        : journey.attributes;
    const liveEvents =
      span.events && Array.isArray(span.events) ? span.events.slice() : [];

    out.push({
      name: journey.name,
      spanContext: () => journey.spanContext,
      parentSpanContext,
      parentSpanId: parentSpanContext && parentSpanContext.spanId,
      startTime: journey.startTime,
      endTime: journey.startTime,
      attributes: liveAttrs || {},
      events: liveEvents,
      status: { code: 0 },
      kind: 0
    });
    inBatch.add(sid);
  });
  return out;
}

export function markOpenJourneysExported(exportedSpans) {
  (exportedSpans || []).forEach(synthetic => {
    const sid =
      synthetic && synthetic.spanContext && synthetic.spanContext().spanId;
    if (!sid) return;
    JOURNEY_EXPORT_ORDER.forEach(key => {
      const journey = journeys[key];
      if (
        journey &&
        journey.spanContext &&
        journey.spanContext.spanId === sid
      ) {
        journey.exportInjected = true;
      }
    });
  });
}

export function hasOpenJourneysNeedingExport() {
  return JOURNEY_EXPORT_ORDER.some(key => {
    const journey = journeys[key];
    return Boolean(journey && journey.span && !journey.exportInjected);
  });
}

export function getJourneySpan(journeyKey) {
  const journey = journeys[journeyKey];
  return journey && journey.span ? journey.span : null;
}

/**
 * SpanContext for QR handoff links: prefer open handoff journey, else session.
 */
export function getHandoffParentSpanContext() {
  const handoff = getJourneySpan(JOURNEY.HANDOFF);
  if (handoff && typeof handoff.spanContext === 'function') {
    return handoff.spanContext();
  }
  try {
    const { getSessionSpan } = require('./sessionTrace');
    const session = getSessionSpan && getSessionSpan();
    if (session && typeof session.spanContext === 'function') {
      return session.spanContext();
    }
  } catch (e) {
    // ignore
  }
  return null;
}

export function runInJourney(journeyKey, fn) {
  const journey = journeys[journeyKey];
  if (!journey) {
    return typeof fn === 'function' ? fn(null) : undefined;
  }
  return context.with(journey.ctx, () => fn(journey.span));
}

export function endJourney(journeyKey, outcome, detail) {
  const journey = journeys[journeyKey];
  if (!journey) return;
  delete journeys[journeyKey];

  // Close nested send stack first (leaf → parent), then finish this span.
  if (journeyKey === JOURNEY.PREPARE && journeys[JOURNEY.CREDENTIALS]) {
    endJourney(JOURNEY.CREDENTIALS, outcome, detail);
  }
  if (journeyKey === JOURNEY.SUBMIT) {
    if (journeys[JOURNEY.CREDENTIALS]) {
      endJourney(JOURNEY.CREDENTIALS, outcome, detail);
    }
    if (journeys[JOURNEY.PREPARE]) {
      endJourney(JOURNEY.PREPARE, outcome, detail);
    }
  }

  const { span } = journey;
  if (!span) return;

  if (outcome === 'error') {
    failSpan(span, detail instanceof Error ? detail : detail || 'error');
  } else if (outcome === 'reject') {
    rejectSpan(span, detail);
  } else {
    okSpan(span, typeof detail === 'object' ? detail : undefined);
  }
  span.end();
}

export function setJourneyAttributes(journeyKey, attributes) {
  const span = getJourneySpan(journeyKey);
  if (span) setAttrs(span, attributes);
  const journey = journeys[journeyKey];
  if (journey && attributes) {
    journey.attributes = {
      ...journey.attributes,
      ...sanitizeAttributes(attributes)
    };
  }
}

export function beginDocument(attributes) {
  return beginJourney(JOURNEY.DOCUMENT, 'document', attributes);
}

export function endDocument(outcome, detail) {
  endJourney(JOURNEY.DOCUMENT, outcome, detail);
}

/** Breadcrumb on the deepest active business span. */
export function addTraceEvent(name, attributes) {
  if (!isTelemetryStarted()) return;
  const span =
    trace.getActiveSpan() ||
    getJourneySpan(JOURNEY.CREDENTIALS) ||
    getJourneySpan(JOURNEY.PREPARE) ||
    getJourneySpan(JOURNEY.HANDOFF) ||
    getJourneySpan(JOURNEY.DOCUMENT) ||
    getJourneySpan(JOURNEY.SUBMIT) ||
    getJourneySpan(JOURNEY.LOAD);
  if (span && span.isRecording && span.isRecording()) {
    span.addEvent(name, sanitizeAttributes(attributes));
    return;
  }
  // No open journey — short root marker still tagged with session id.
  const eventSpan = getTracer().startSpan(
    name,
    { attributes: sanitizeAttributes(attributes) },
    ROOT_CONTEXT
  );
  eventSpan.end();
}
